
import React, { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

// Define validation schema
const inviteSchema = z.object({
  inviteCode: z.string().min(6, { message: "Invite code must be at least 6 characters" }),
});

type InviteFormValues = z.infer<typeof inviteSchema>;

interface InviteCodeFormProps {
  onValidateInvite: (code: string) => Promise<boolean>;
  onSwitchToLogin: () => void;
  onSwitchToRequestInvite: () => void;
  onValidInvite: (code: string) => void;
}

const InviteCodeForm: React.FC<InviteCodeFormProps> = ({
  onValidateInvite,
  onSwitchToLogin,
  onSwitchToRequestInvite,
  onValidInvite
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  // Initialize react-hook-form
  const form = useForm<InviteFormValues>({
    resolver: zodResolver(inviteSchema),
    defaultValues: {
      inviteCode: '',
    },
  });

  const handleSubmit = async (values: InviteFormValues) => {
    setIsLoading(true);
    setInviteError(null);
    
    try {
      const isValid = await onValidateInvite(values.inviteCode.trim());
      if (isValid) {
        onValidInvite(values.inviteCode.trim());
      } else {
        setInviteError("This invite code is invalid or has expired.");
      }
    } catch (error) {
      console.error('Error validating invite:', error);
      setInviteError("Unable to validate invite code.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h1 className="text-3xl font-display font-semibold text-hormony-charcoal">
          Invite Required
        </h1>
        <p className="mt-2 text-hormony-charcoal/70">
          Registration is currently invite-only. Please enter your invite code to continue.
        </p>
      </div>
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="inviteCode"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-hormony-charcoal">
                  Invite Code
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="text"
                    placeholder="Enter your invite code"
                    className="appearance-none rounded-lg relative block w-full"
                  />
                </FormControl>
                <FormMessage />
                {inviteError && (
                  <div className="mt-2 text-red-500 text-sm flex items-center">
                    <AlertCircle size={16} className="mr-1" />
                    {inviteError}
                  </div>
                )}
              </FormItem>
            )}
          />
          
          <div>
            <Button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full flex items-center justify-center"
            >
              {isLoading ? (
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : null}
              Validate Invite Code
            </Button>
          </div>
          
          <div className="text-center mt-4">
            <button
              type="button"
              className="text-sm text-hormony-charcoal/70 hover:text-hormony-coral transition-colors"
              onClick={onSwitchToLogin}
            >
              Already have an account? Sign in
            </button>
          </div>
          
          <div className="text-center mt-2">
            <button
              type="button"
              className="text-sm text-hormony-charcoal/70 hover:text-hormony-coral transition-colors"
              onClick={onSwitchToRequestInvite}
            >
              Don't have an invite? Request one
            </button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default InviteCodeForm;
